import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Popover,
  PopoverButton,
  PopoverPanel,
} from "@headlessui/react";
import { Check, ChevronDown, Search } from "lucide-react";
import { useRef, useState } from "react";

import type { CountryCode } from "@/config/countries";
import { PHONE_REGIONS } from "@/utils/phone";

type Props = {
  value: CountryCode;
  onChange: (country: CountryCode) => void;
  disabled?: boolean;
};

function CountrySearch({
  value,
  onChange,
  onClose,
}: Omit<Props, "disabled"> & { onClose: () => void }) {
  const [query, setQuery] = useState("");
  const search = query.trim().toLowerCase();
  const countries = PHONE_REGIONS.filter(
    (country) =>
      country.name.toLowerCase().includes(search) ||
      country.alpha2.toLowerCase().includes(search) ||
      country.callingCode.includes(search),
  );

  return (
    <Combobox
      value={value}
      onChange={(country) => country && onChange(country)}
      onClose={onClose}
      immediate
    >
      <div className="login-country-search-wrap">
        <Search size={16} aria-hidden="true" />
        <ComboboxInput
          autoFocus
          aria-label="Search countries"
          placeholder="Search country or dial code"
          className="login-country-search"
          autoComplete="off"
          displayValue={() => ""}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>
      <ComboboxOptions
        static
        modal={false}
        className="login-country-options"
        aria-label="Countries"
      >
        {countries.map((country) => (
          <ComboboxOption
            key={country.alpha2}
            value={country.alpha2}
            className="login-country-option"
            aria-label={`${country.name} (${country.alpha2}), ${country.callingCode}`}
          >
            {({ selected }) => (
              <>
                <span className="login-country-flag" aria-hidden="true">
                  {country.flag}
                </span>
                <span className="login-country-name">{country.name}</span>
                <span className="login-country-dial">{country.callingCode}</span>
                <span className="login-country-check" aria-hidden="true">
                  {selected && <Check size={16} />}
                </span>
              </>
            )}
          </ComboboxOption>
        ))}
      </ComboboxOptions>
      {countries.length === 0 && (
        <p className="login-country-empty" role="status">
          No countries found.
        </p>
      )}
    </Combobox>
  );
}

export function LoginCountrySelect({ value, onChange, disabled }: Props) {
  const country = PHONE_REGIONS.find((region) => region.alpha2 === value);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  return (
    // Closing the picker when disabled prevents country changes during OTP entry.
    <Popover key={disabled ? "disabled" : "enabled"} className="login-country-select">
      {({ close }) => (
        <>
          <PopoverButton
            ref={triggerRef}
            id="login-country"
            disabled={disabled}
            className="login-country-trigger"
            aria-label={`Country: ${country?.name ?? value} (${country?.callingCode ?? value})`}
            aria-haspopup="dialog"
            title={country?.name ?? value}
          >
            <span className="login-country-flag" aria-hidden="true">
              {country?.flag}
            </span>
            <span>{country?.callingCode ?? value}</span>
            <ChevronDown size={14} aria-hidden="true" />
          </PopoverButton>
          <PopoverPanel
            ref={panelRef}
            portal
            focus
            anchor={{ to: "bottom start", gap: 8, padding: 12 }}
            role="dialog"
            aria-label="Choose phone country"
            className="login-country-panel"
          >
            <CountrySearch
              value={value}
              onChange={onChange}
              onClose={() => {
                // Let an outside click finish moving focus before closing the search.
                requestAnimationFrame(() => {
                  if (!triggerRef.current?.isConnected) return;
                  const focused = document.activeElement;
                  close(
                    focused instanceof HTMLElement &&
                      focused !== document.body &&
                      !panelRef.current?.contains(focused)
                      ? focused
                      : undefined,
                  );
                });
              }}
            />
          </PopoverPanel>
        </>
      )}
    </Popover>
  );
}
